// src/Dashboard/pages/teacher/SuiviCompetitions/components/CompetitionResultDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaEdit,
  FaMedal,
  FaSave,
  FaTimes,
  FaTrophy,
  FaUndo,
  FaUserTie,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { updateAdherentResults } from "../../../../../services/eventServices";
import {
  addPalmares,
  checkPalmaresExists,
  updatePalmares,
} from "../../../../../services/palmaresServices";

import {
  formatDateLongFR,
  getAdherentCompetitionCategories,
  getAdherentName,
  getAdherentPhoto,
  isCoachForEvent,
} from "../utils/suiviCompetitionsUtils";

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100";

const RESULT_TAGS = [
  { label: "Gagné", value: "✅", tone: "bg-emerald-600 hover:bg-emerald-700" },
  { label: "Perdu", value: "❌", tone: "bg-red-600 hover:bg-red-700" },
  { label: "Nul", value: "N", tone: "bg-amber-500 hover:bg-amber-600" },
  { label: "Forfait", value: "F", tone: "bg-slate-600 hover:bg-slate-700" },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

export default function CompetitionResultDrawer({
  isOpen,
  event,
  adherent,
  results,
  loading,
  enseignants = [],
  onClose,
  onSaved,
}) {
  const [formData, setFormData] = useState({
    resultats: "",
    classement: "",
    observations: "",
    coach_hour: 0,
  });

  const [saving, setSaving] = useState(false);
  const [palmaresExists, setPalmaresExists] = useState(false);
  const [palmaresId, setPalmaresId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      resultats: results?.resultats || "",
      classement: results?.classement || "",
      observations: results?.observations || "",
      coach_hour: results?.coach_hour || 0,
    });
  }, [isOpen, results]);

  useEffect(() => {
    if (!isOpen || !adherent?.id || !event?.id) return;

    let alive = true;

    const check = async () => {
      try {
        const response = await checkPalmaresExists(adherent.id, event.id);

        if (!alive) return;

        if (response?.exists) {
          setPalmaresExists(true);
          setPalmaresId(response.palmaresId ?? null);
        } else {
          setPalmaresExists(false);
          setPalmaresId(null);
        }
      } catch {
        if (!alive) return;

        setPalmaresExists(false);
        setPalmaresId(null);
      }
    };

    check();

    return () => {
      alive = false;
    };
  }, [isOpen, adherent?.id, event?.id]);

  const isCoach = useMemo(
    () => isCoachForEvent(adherent, enseignants),
    [adherent, enseignants]
  );

  if (!isOpen || !event || !adherent) return null;

  const photo = getAdherentPhoto(adherent);
  const name = getAdherentName(adherent);
  const { ageCategory, weightCategory } = getAdherentCompetitionCategories(adherent);

  const isFormFilled =
    !!formData.resultats || !!formData.classement || !!formData.observations;

  const addResultTag = (tag) => {
    setFormData((previous) => {
      const current = String(previous.resultats || "")
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean);

      return {
        ...previous,
        resultats: [...current, tag].join(" | "),
      };
    });
  };

  const removeLastTag = () => {
    setFormData((previous) => {
      const current = String(previous.resultats || "")
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean);

      current.pop();

      return {
        ...previous,
        resultats: current.join(" | "),
      };
    });
  };

  const handleChange = (eventChange) => {
    const { name, value } = eventChange.target;

    setFormData((previous) => ({
      ...previous,
      [name]: name === "coach_hour" ? Number(value || 0) : value,
    }));
  };

  const handleSaveResults = async () => {
    if (!adherent?.id || !event?.id) {
      toast.error("Données incomplètes.");
      return;
    }

    try {
      setSaving(true);

      await updateAdherentResults({
        adherent_id: adherent.id,
        event_id: event.id,
        resultats: formData.resultats,
        classement: formData.classement,
        observations: formData.observations,
        coach_hour: formData.coach_hour || 0,
      });

      toast.success("Résultats enregistrés.");
      await onSaved?.();
    } catch (error) {
      console.error("Erreur sauvegarde résultats :", error);
      toast.error("Erreur lors de l’enregistrement des résultats.");
    } finally {
      setSaving(false);
    }
  };

  const handlePalmaresAction = async () => {
    if (!adherent?.id || !event?.id) {
      toast.error("Données incomplètes.");
      return;
    }

    const payload = {
      adherent_palmares_id: adherent.id,
      event_palmares_id: event.id,
      date_palmares: event.start || new Date().toISOString(),
      titre_palmares: event.titre,
      resultats_palmares: formData.resultats,
      classement_palmares: formData.classement,
      observations_palmares: formData.observations,
    };

    try {
      setSaving(true);

      if (palmaresExists) {
        if (!palmaresId) {
          toast.error("ID palmarès manquant.");
          return;
        }

        await updatePalmares(palmaresId, payload);
        toast.success("Palmarès mis à jour.");
      } else {
        await addPalmares(payload);
        toast.success("Résultat ajouté au palmarès.");
        setPalmaresExists(true);
      }

      await onSaved?.();
    } catch (error) {
      console.error("Erreur palmarès :", error);
      toast.error("Erreur lors de l’ajout / mise à jour du palmarès.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Résultats compétition
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {event?.titre || "Compétition"}
            </h2>

            <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
              {formatDateLongFR(event?.start)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="space-y-4">
              <div className="h-32 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
              <div className="h-64 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-3">
              <section className="lg:col-span-1">
                <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={photo}
                      alt={name}
                      className="h-full w-full object-cover"
                      onError={(eventImg) => {
                        eventImg.currentTarget.src = "/img/user_avatar.jpg";
                      }}
                    />

                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 via-slate-950/45 to-transparent p-4 pt-16">
                      <p className="text-lg font-bold text-white">{name}</p>
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <div className="flex flex-wrap gap-2">
                      {isCoach ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">
                          <FaUserTie />
                          Coach
                        </span>
                      ) : (
                        <>
                          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/50">
                            {ageCategory}
                          </span>

                          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/50">
                            {weightCategory}
                          </span>
                        </>
                      )}
                    </div>

                    {isCoach && (
                      <Field label="Heures de coaching">
                        <input
                          type="number"
                          name="coach_hour"
                          min="0"
                          value={formData.coach_hour}
                          onChange={handleChange}
                          className={inputClass}
                        />
                      </Field>
                    )}
                  </div>
                </div>
              </section>

              <section className="space-y-5 lg:col-span-2">
                <div className="rounded-[26px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    Raccourcis résultats
                  </h3>

                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Utilisez les raccourcis pour noter rapidement les combats.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {RESULT_TAGS.map((tag) => (
                      <button
                        key={tag.label}
                        type="button"
                        onClick={() => addResultTag(tag.value)}
                        className={`inline-flex items-center rounded-2xl px-4 py-3 text-sm font-bold text-white transition ${tag.tone}`}
                      >
                        {tag.label}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={removeLastTag}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                    >
                      <FaUndo />
                      Retirer dernier
                    </button>
                  </div>
                </div>

                <div className="rounded-[26px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Résultats">
                      <input
                        type="text"
                        name="resultats"
                        value={formData.resultats}
                        onChange={handleChange}
                        placeholder="Ex : ✅ | ❌ | ✅"
                        className={inputClass}
                      />
                    </Field>

                    <Field label="Classement">
                      <input
                        type="text"
                        name="classement"
                        value={formData.classement}
                        onChange={handleChange}
                        placeholder="Ex : 1er, 2e, 3e, NC..."
                        className={inputClass}
                      />
                    </Field>

                    <Field label="Observations">
                      <textarea
                        name="observations"
                        value={formData.observations}
                        onChange={handleChange}
                        rows={6}
                        placeholder="Notes d’analyse, comportement, axes de travail..."
                        className={inputClass}
                      />
                    </Field>
                  </div>
                </div>

                <div className="rounded-[26px] border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-900/10">
                  <div className="flex items-start gap-3">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-amber-500 text-white">
                      <FaTrophy />
                    </span>

                    <div>
                      <h3 className="font-bold text-amber-950 dark:text-amber-100">
                        Palmarès
                      </h3>

                      <p className="mt-1 text-sm leading-6 text-amber-800/80 dark:text-amber-100/70">
                        Une fois le résultat saisi, vous pouvez l’ajouter au
                        palmarès de l’adhérent. Si un palmarès existe déjà pour
                        cette compétition, il sera mis à jour.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={handleSaveResults}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Sauvegarder"}
            </button>

            {isFormFilled && (
              <button
                type="button"
                onClick={handlePalmaresAction}
                disabled={saving || loading}
                className="inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {palmaresExists ? <FaEdit /> : <FaMedal />}
                {palmaresExists ? "Màj palmarès" : "Ajouter palmarès"}
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}